import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import type { MatchDetailRow, MatchHistoryPlayer } from '../lib/api.js';
import { useAuth } from '../stores/auth.js';
import { ChipIcon } from "../components/ChipIcon.js";

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function durationText(start: string, end: string | null): string {
  if (!end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms <= 0) return '—';
  const mins = Math.floor(ms / 60_000);
  const secs = Math.floor((ms % 60_000) / 1000);
  return mins > 0 ? `${mins} мин ${secs.toString().padStart(2, '0')} с` : `${secs} с`;
}

export function MatchDetail() {
  const { id } = useParams<{ id: string }>();
  const me = useAuth((s) => s.user);
  const [match, setMatch] = useState<MatchDetailRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setError(null);
    api
      .matchDetail(id)
      .then((m) => {
        if (cancelled) return;
        setMatch(m);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'не удалось загрузить');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (error) {
    return (
      <div className="mx-auto flex w-full max-w-[420px] flex-col gap-4 px-5 py-6">
        <p
          className="rounded-2xl px-4 py-3 text-sm text-coral"
          style={{
            background: 'var(--color-glass)',
            border: '1px solid color-mix(in oklab, var(--color-coral) 35%, transparent)',
          }}
        >
          {error}
        </p>
        <Link to="/history" className="btn-ghost text-center">
          Назад к истории
        </Link>
      </div>
    );
  }
  if (!match || !me) {
    return <p className="px-5 py-6 text-sm text-ivory/60">Загружаем…</p>;
  }

  const myRow = match.players.find((p) => p.userId === me.id);
  const isDraw = match.isDraw;
  const isLoser = match.loserId === me.id;
  const headline = isDraw ? 'Ничья' : isLoser ? 'Поражение' : match.loserId ? 'Победа' : 'В процессе';
  const accent = isDraw
    ? 'var(--color-ivory)'
    : isLoser
      ? 'var(--color-coral)'
      : match.loserId
        ? 'var(--color-gold)'
        : 'var(--color-ivory)';

  // Sort players by finish (1, 2, 3 …, then loser at the end).
  const sorted = [...match.players].sort((a, b) => {
    const ap = a.finishPlace ?? Number.POSITIVE_INFINITY;
    const bp = b.finishPlace ?? Number.POSITIVE_INFINITY;
    if (ap !== bp) return ap - bp;
    return a.seat - b.seat;
  });

  return (
    <div className="mx-auto flex w-full max-w-[420px] flex-col gap-5 px-5 py-6">
      <div className="flex items-center justify-between">
        <Link to="/history" className="text-sm text-ivory/60 hover:underline">
          ← История
        </Link>
        <span className="text-xs text-ivory/55">{formatDate(match.finishedAt ?? match.createdAt)}</span>
      </div>

      <div
        className="rounded-3xl px-5 py-5"
        style={{
          background: 'var(--color-glass)',
          border: '1px solid color-mix(in oklab, var(--color-gold) 30%, transparent)',
        }}
      >
        <p className="text-xs uppercase tracking-wider text-ivory/55">Результат</p>
        <p className="heading-display text-3xl" style={{ color: accent }}>
          {headline}
        </p>
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-ivory/70">
          <Cell label="Игроки" value={`${match.playerCount}`} />
          <Cell label="Длилась" value={durationText(match.createdAt, match.finishedAt)} />
          <Cell
            label="Ставка"
            value={
              match.stakeStars > 0 ? (
                <span className="inline-flex items-center gap-1">
                  {match.stakeStars} <ChipIcon size={11} />
                </span>
              ) : (
                'Free'
              )
            }
          />
        </div>
      </div>

      <section className="flex flex-col gap-2">
        <h2 className="heading-display text-xl text-gold">Участники</h2>
        <ul className="flex flex-col gap-2">
          {sorted.map((p) => (
            <PlayerLine key={p.userId} player={p} isLoser={p.userId === match.loserId} isMe={p.userId === me.id} />
          ))}
        </ul>
      </section>

      {myRow?.finishPlace !== undefined && myRow?.finishPlace !== null && (
        <p className="text-center text-xs text-ivory/55">
          Ваше место: {myRow.finishPlace}
        </p>
      )}
    </div>
  );
}

function Cell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wider text-ivory/45">{label}</span>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: 'var(--color-ivory)' }}>
        {value}
      </span>
    </div>
  );
}

function PlayerLine({
  player,
  isLoser,
  isMe,
}: {
  player: MatchHistoryPlayer;
  isLoser: boolean;
  isMe: boolean;
}) {
  const place = player.finishPlace;
  const placeLabel = isLoser ? 'дурак' : place === null ? '—' : `#${place}`;
  const placeColor = isLoser
    ? 'var(--color-coral)'
    : place === 1
      ? 'var(--color-gold)'
      : 'var(--color-ivory)';
  const initial = player.firstName.slice(0, 1).toUpperCase();

  return (
    <li
      className="flex items-center gap-3 rounded-2xl px-3 py-2.5"
      style={{
        background: 'var(--color-glass)',
        border: isMe
          ? '1px solid color-mix(in oklab, var(--color-gold) 55%, transparent)'
          : '1px solid color-mix(in oklab, var(--color-gold) 18%, transparent)',
      }}
    >
      <div
        className="grid h-9 w-9 place-items-center overflow-hidden rounded-full"
        style={{ border: '1px solid var(--color-gold)', background: 'var(--color-felt-deep)' }}
      >
        {player.photoUrl ? (
          <img src={player.photoUrl} alt={player.firstName} className="h-full w-full object-cover" />
        ) : (
          <span className="text-sm text-gold" style={{ fontFamily: 'var(--font-display)' }}>
            {initial}
          </span>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm" style={{ fontFamily: 'var(--font-display)', fontSize: 15 }}>
          {player.firstName}
          {isMe && <span className="text-ivory/45"> · вы</span>}
        </span>
        {player.username && <span className="truncate text-xs text-ivory/55">@{player.username}</span>}
      </div>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: 14, color: placeColor }}>
        {placeLabel}
      </span>
    </li>
  );
}
